import React, { useState, useEffect } from 'react';
import { Clock, Calendar, Tag, Flame, Loader2, Star, ExternalLink } from 'lucide-react';

const COUNTRY_MAP: Record<number, string> = {
  1: 'Египет', 2: 'Таиланд', 3: 'Индия', 4: 'Турция', 5: 'Тунис',
  7: 'Индонезия', 8: 'Мальдивы', 9: 'ОАЭ', 10: 'Куба', 12: 'Шри-Ланка',
  13: 'Китай', 16: 'Вьетнам', 21: 'Черногория', 27: 'Маврикий',
  28: 'Сейшелы', 41: 'Танзания', 46: 'Абхазия', 47: 'Россия',
  59: 'Бахрейн', 64: 'Оман', 78: 'Казахстан', 79: 'Катар', 90: 'Венесуэла',
};

const MEAL_MAP: Record<number, string> = {
  1: 'Без питания', 2: 'Завтрак', 3: 'Завтрак', 4: 'Полупансион',
  5: 'Полный пансион', 6: 'Ультра все включено', 7: 'Все включено',
};

interface HotTourItem {
  id: number;
  tour_id: number;
  country_id: number;
  hotel_id: number;
  fly_date: string;
  nights: number;
  meal_id: number;
  meal_group_id: number;
  stars: number;
  old_price: number;
  price: number;
  currency_id: number;
  title: string;
  description: string;
  picture: { id: number; path: string } | null;
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
};

const formatPrice = (price: number) => Math.round(price).toLocaleString('ru-RU');

const HotTours: React.FC = () => {
  const [tours, setTours] = useState<HotTourItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    const fetchHotTours = async () => {
      try {
        const res = await fetch('/api/hot-tours');
        const data = await res.json();
        const items = data.items || data || [];
        setTours(Array.isArray(items) ? items : []);
      } catch {
        setError('Не удалось загрузить горящие туры');
      } finally {
        setLoading(false);
      }
    };
    fetchHotTours();
  }, []);

  if (loading) {
    return (
      <section id="hot-tours" className="py-20 bg-white">
        <div className="container-custom text-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-neutral-500">Загружаем горящие туры...</p>
        </div>
      </section>
    );
  }

  if (error || tours.length === 0) {
    return (
      <section id="hot-tours" className="py-20 bg-white">
        <div className="container-custom text-center py-12">
          <Flame className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-500">{error || 'Горящие туры пока не найдены'}</p>
        </div>
      </section>
    );
  }

  const visibleTours = tours.slice(0, visibleCount);

  return (
    <section id="hot-tours" className="py-20 bg-white">
      <div className="container-custom">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 gap-6">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Flame className="w-6 h-6 text-red-500" />
              <span className="text-red-500 font-semibold uppercase text-sm tracking-wider">Горящие предложения</span>
            </div>
            <h2 className="section-title">Горящие туры из Алматы</h2>
            <p className="text-neutral-500 max-w-2xl">
              Реальные предложения от туроператоров с актуальными ценами. Успейте забронировать!
            </p>
          </div>
          <div className="text-sm text-neutral-400">
            Найдено: {tours.length} предложений
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleTours.map((tour) => {
            const discount = tour.old_price > 0
              ? Math.round((1 - tour.price / tour.old_price) * 100)
              : 0;
            const country = COUNTRY_MAP[tour.country_id] || `Страна #${tour.country_id}`;
            const meal = MEAL_MAP[tour.meal_id] || MEAL_MAP[tour.meal_group_id] || '';

            return (
              <div key={tour.id} className="tour-card group relative overflow-hidden">
                {/* Discount badge */}
                {discount > 0 && (
                  <div className="absolute top-4 left-4 z-10 bg-red-500 text-white px-3 py-1.5 rounded-full flex items-center font-bold text-sm shadow-lg shadow-red-500/30">
                    <Tag className="w-3.5 h-3.5 mr-1" />
                    -{discount}%
                  </div>
                )}

                {/* Stars */}
                {tour.stars > 0 && (
                  <div className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 flex items-center gap-0.5 shadow">
                    {[...Array(tour.stars)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                )}

                {/* Image */}
                <div className="relative h-48 overflow-hidden bg-neutral-100">
                  {tour.picture?.path ? (
                    <img
                      src={tour.picture.path}
                      alt={tour.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200">
                      <Flame className="w-12 h-12 text-primary-300" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-4 text-white">
                    <div className="text-lg font-bold">{country}</div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="font-bold text-neutral-800 mb-2 line-clamp-2 text-sm leading-snug group-hover:text-primary-500 transition-colors">
                    {tour.title}
                  </h3>

                  <div className="flex items-center gap-3 text-xs text-neutral-500 mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 text-primary-500" />
                      {formatDate(tour.fly_date)}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5 text-primary-500" />
                      {tour.nights} ночей
                    </div>
                    {meal && (
                      <span className="bg-primary-50 text-primary-600 px-2 py-0.5 rounded text-[11px] font-medium">
                        {meal}
                      </span>
                    )}
                  </div>

                  {/* Price */}
                  <div className="flex items-end justify-between">
                    <div>
                      {tour.old_price > 0 && tour.old_price !== tour.price && (
                        <div className="text-xs text-neutral-400 line-through">
                          {formatPrice(tour.old_price)} ₸
                        </div>
                      )}
                      <div className="text-xl font-bold text-secondary-300">
                        {formatPrice(tour.price)} ₸
                      </div>
                    </div>
                    <a
                      href={`https://wa.me/77019823126?text=${encodeURIComponent(`Здравствуйте! Интересует горящий тур: ${tour.title}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary text-xs py-2 px-4"
                    >
                      Забронировать
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {visibleCount < tours.length && (
          <div className="mt-10 text-center">
            <button
              onClick={() => setVisibleCount(prev => prev + 6)}
              className="btn btn-outline px-10"
            >
              Показать ещё ({tours.length - visibleCount} туров)
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default HotTours;
